import {
  createEffect,
  createResource,
  createSignal,
  type JSX,
  onCleanup,
  onMount,
  Show,
} from "solid-js";
import { acceptSession, isAdmin } from "../../store/admin";
import { api } from "../../store/api";
import { errorMessage } from "../../store/api-client";
import {
  demoCapabilities,
  initializeDemo,
  refreshCapabilities,
  startCapabilityRefresh,
} from "../../store/demo";
import { queryClient, queryPersister } from "../../store/query";

export function Gate(props: { children: JSX.Element }) {
  const [ready, { refetch }] = createResource(initializeDemo);
  const [password, setPassword] = createSignal("");
  const [showPassword, setShowPassword] = createSignal(false);
  const environmentName = () => {
    const id = demoCapabilities()?.demoId;
    if (!id) return "Faro";
    if (id === "main") return "Faro Core";
    return id.charAt(0).toUpperCase() + id.slice(1).replaceAll("-", " ");
  };
  const [error, setError] = createSignal("");
  const [busy, setBusy] = createSignal(false);
  const refresh = () => {
    void refreshCapabilities().catch(() => undefined);
  };
  createEffect(() => {
    if (demoCapabilities()) onCleanup(startCapabilityRefresh());
  });
  onMount(() => {
    window.addEventListener("focus", refresh);
    window.addEventListener("online", refresh);
    onCleanup(() => {
      window.removeEventListener("focus", refresh);
      window.removeEventListener("online", refresh);
      queryClient.clear();
    });
  });
  const submit = async (event: SubmitEvent) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const identity = await refreshCapabilities();
      const session = await api.adminLogin(password());
      acceptSession(session, identity);
      setPassword("");
      setShowPassword(false);
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  };
  createEffect(() => {
    if (!ready.loading && !isAdmin()) {
      queryClient.clear();
      void queryPersister.removeClient();
    }
  });
  return (
    <Show
      when={!ready.loading && !ready.error && isAdmin()}
      fallback={
        <main class="gate">
          <form class="gate-card card" onSubmit={submit}>
            <h1 class="landing-title">{environmentName()}</h1>
            <p class="landing-sub">Enter this site's password to sign in.</p>
            <Show when={!ready.loading} fallback={<p>Connecting…</p>}>
              <Show
                when={!ready.error && demoCapabilities()}
                fallback={
                  <>
                    <p role="alert">Cannot connect to this environment.</p>
                    <button
                      type="button"
                      class="btn"
                      onClick={() => void refetch()}
                    >
                      Retry
                    </button>
                  </>
                }
              >
                <label for="gate-password">Password</label>
                <input
                  id="gate-password"
                  name="password"
                  class="gate-input"
                  type={showPassword() ? "text" : "password"}
                  placeholder="Passphrase"
                  autocomplete="current-password"
                  autocapitalize="off"
                  spellcheck={false}
                  required
                  value={password()}
                  onInput={(event) => setPassword(event.currentTarget.value)}
                />
                <label>
                  <input
                    type="checkbox"
                    checked={showPassword()}
                    onChange={(event) =>
                      setShowPassword(event.currentTarget.checked)
                    }
                  />{" "}
                  Show password
                </label>
                <button class="btn btn-primary" type="submit" disabled={busy()}>
                  {busy() ? "Signing in…" : "Sign in"}
                </button>
              </Show>
            </Show>
            <div class="gate-error" role="alert">
              {error()}
            </div>
          </form>
        </main>
      }
    >
      {props.children}
    </Show>
  );
}
